// CompletionDefectsSection.js
// ─────────────────────────────────────────────────────────────────────────────
// COMPLETION & DEFECTS  (GCC 2015)
//
// Three linked things an RE manages from Practical Completion onward:
//   1. Certificate of Practical Completion — date of completion, certificate
//      reference, who issued it. This date starts everything below.
//   2. Defects Liability Period (DLP) — runs from the PC date for the contract
//      DLP (GCC default 12 months). Live countdown to the end of DLP, at which
//      the Final Approval / Defects Certificate falls due.
//   3. Snag / defects list — one row per defect, with location, target date and
//      close-out status. Open vs overdue vs closed.
//   4. Retention release milestones (GCC clause 6.10.3): half the retention is
//      released at Practical Completion; the balance at the end of the DLP.
//      Both milestones tracked with due dates, amounts and released status.
//
// Firestore:
//   projects/{projectId}/completion/summary  — single doc (PC + DLP + retention)
//   projects/{projectId}/defects             — one doc per defect / snag
//
// Self-contained: reads/writes its own data, takes only the project.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo } from "react";
import {
  collection, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc,
  onSnapshot, serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";

const C = tokens.color;
const MONO = tokens.font?.mono || '"JetBrains Mono", monospace';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const numberOrZero = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmtR = (v) => "R " + numberOrZero(v).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
};

const DAY = 86400000;
const daysBetween = (aISO, bISO) => {
  if (!aISO || !bISO) return null;
  const a = new Date(aISO + "T00:00:00"), b = new Date(bISO + "T00:00:00");
  if (isNaN(a) || isNaN(b)) return null;
  return Math.round((b - a) / DAY);
};

// PC date + N months → ISO end-of-DLP date.
function addMonths(iso, months) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return "";
  d.setMonth(d.getMonth() + numberOrZero(months));
  return d.toISOString().slice(0, 10);
}

const DEFECT_STATUSES = ["Open", "In Progress", "Closed"];
const STATUS_META = {
  "Open":        { bg: C.warningSoft, text: C.warning, border: C.warningBorder },
  "In Progress": { bg: C.infoSoft,    text: C.info,    border: C.infoBorder },
  "Closed":      { bg: C.successSoft, text: C.success, border: C.successBorder },
  "Overdue":     { bg: C.dangerSoft,  text: C.danger,  border: C.dangerBorder },
};

function defectState(d) {
  if (String(d.status) === "Closed") return "Closed";
  if (d.targetDate && daysBetween(todayISO(), d.targetDate) < 0) return "Overdue";
  return d.status || "Open";
}

// ─── Shared styles ───────────────────────────────────────────────────────────

const s = {
  page: { display: "grid", gap: 20, minWidth: 0, fontFamily: tokens.font?.body || '"Inter", sans-serif', color: C.ink },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, boxShadow: tokens.shadow?.sm, minWidth: 0 },
  title: { margin: 0, fontSize: 24, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  h3: { margin: "0 0 12px", fontSize: 13, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" },
  lbl: { display: "block", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, marginBottom: 4 },
  inp: { width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 12.5, color: C.ink, fontFamily: "inherit", outline: "none" },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnAccent: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnSmall: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  btnDanger: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.dangerBorder}`, background: C.dangerSoft, color: C.danger, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  th: { padding: "8px 10px", textAlign: "left", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  td: { padding: "8px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  chip: (m) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.03em", background: m.bg, color: m.text, border: `1px solid ${m.border}`, whiteSpace: "nowrap" }),
  mono: { fontFamily: MONO, fontVariantNumeric: "tabular-nums" },
  statTile: { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", minWidth: 0 },
  statLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, margin: 0 },
  statValue: { fontSize: 20, fontWeight: 800, fontFamily: MONO, color: C.ink, margin: "2px 0 0" },
  milestone: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.surfaceAlt, flexWrap: "wrap" },
};

const blankSummary = () => ({
  pcCertificateNo: "", pcDate: "", pcIssuedBy: "", dlpMonths: 12,
  retentionHeld: "", release1Done: false, release1Date: "", release2Done: false, release2Date: "",
  finalApprovalCertNo: "",
});

const blankDefect = () => ({
  ref: "", description: "", location: "", drawingRef: "",
  raisedDate: todayISO(), targetDate: "", status: "Open", closedDate: "",
});

function nextDefectRef(items) {
  const nums = items.map((d) => parseInt((d.ref || "").replace(/[^0-9]/g, ""), 10)).filter(Number.isFinite);
  return `DEF-${String(nums.length ? Math.max(...nums) + 1 : 1).padStart(3, "0")}`;
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════════
function CompletionDefectsSection({ currentProject, currentProjectId }) {
  const projectId = currentProject?.id || currentProjectId;

  const [summary, setSummary] = useState(blankSummary);
  const [defects, setDefects] = useState([]);
  const [defForm, setDefForm] = useState(blankDefect);
  const [editingId, setEditingId] = useState(null);
  const [savingSummary, setSavingSummary] = useState(false);

  // Load the completion summary doc.
  useEffect(() => {
    if (!projectId) { setSummary(blankSummary()); return undefined; }
    const ref = doc(db, "projects", projectId, "completion", "summary");
    const unsub = onSnapshot(ref,
      (snap) => { if (snap.exists()) setSummary({ ...blankSummary(), ...snap.data() }); else setSummary(blankSummary()); },
      (err) => console.error("completion summary listener:", err)
    );
    return unsub;
  }, [projectId]);

  // Load defects.
  useEffect(() => {
    if (!projectId) { setDefects([]); return undefined; }
    const q = query(collection(db, "projects", projectId, "defects"), orderBy("ref"));
    const unsub = onSnapshot(q,
      (snap) => setDefects(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("defects listener:", err)
    );
    return unsub;
  }, [projectId]);

  const dlpEnd = useMemo(() => addMonths(summary.pcDate, summary.dlpMonths), [summary.pcDate, summary.dlpMonths]);
  const dlpDaysLeft = useMemo(() => (dlpEnd ? daysBetween(todayISO(), dlpEnd) : null), [dlpEnd]);

  const retention = numberOrZero(summary.retentionHeld);
  const release1Amt = retention / 2;
  const release2Amt = retention - release1Amt;
  const releasedToDate = (summary.release1Done ? release1Amt : 0) + (summary.release2Done ? release2Amt : 0);
  const retentionOutstanding = retention - releasedToDate;

  const defectStats = useMemo(() => {
    let open = 0, overdue = 0, closed = 0;
    defects.forEach((d) => {
      const st = defectState(d);
      if (st === "Closed") closed += 1;
      else if (st === "Overdue") { overdue += 1; open += 1; }
      else open += 1;
    });
    return { total: defects.length, open, overdue, closed };
  }, [defects]);

  const setSumField = (k, v) => setSummary((p) => ({ ...p, [k]: v }));

  async function saveSummary() {
    if (!projectId) return;
    setSavingSummary(true);
    try {
      const ref = doc(db, "projects", projectId, "completion", "summary");
      const exists = (await getDoc(ref)).exists();
      const payload = { ...summary, dlpEnd, updatedAt: serverTimestamp() };
      if (exists) await updateDoc(ref, payload);
      else await setDoc(ref, { ...payload, createdAt: serverTimestamp() });
    } catch (e) { alert(`Save failed: ${e.message || e}`); }
    finally { setSavingSummary(false); }
  }

  const setDefField = (k, v) => setDefForm((p) => ({ ...p, [k]: v }));

  function resetDefect() { setDefForm(blankDefect()); setEditingId(null); }

  async function saveDefect() {
    if (!projectId) return;
    if (!defForm.description.trim()) { alert("Defect description is required."); return; }
    const ref = (defForm.ref || "").trim() || nextDefectRef(defects);
    const payload = {
      ...defForm, ref,
      closedDate: defForm.status === "Closed" ? (defForm.closedDate || todayISO()) : "",
      updatedAt: serverTimestamp(),
    };
    try {
      if (editingId) await updateDoc(doc(db, "projects", projectId, "defects", editingId), payload);
      else await addDoc(collection(db, "projects", projectId, "defects"), { ...payload, createdAt: serverTimestamp() });
      resetDefect();
    } catch (e) { alert(`Save failed: ${e.message || e}`); }
  }

  function editDefect(d) {
    setDefForm({
      ref: d.ref || "", description: d.description || "", location: d.location || "",
      drawingRef: d.drawingRef || "", raisedDate: d.raisedDate || todayISO(),
      targetDate: d.targetDate || "", status: d.status || "Open", closedDate: d.closedDate || "",
    });
    setEditingId(d.id);
  }

  async function removeDefect(id) {
    if (!projectId) return;
    if (!window.confirm("Delete this defect?")) return;
    try { await deleteDoc(doc(db, "projects", projectId, "defects", id)); }
    catch (e) { alert(`Delete failed: ${e.message || e}`); }
  }

  async function toggleRelease(which) {
    if (!projectId) return;
    const next = { ...summary };
    if (which === 1) { next.release1Done = !summary.release1Done; next.release1Date = next.release1Done ? todayISO() : ""; }
    else { next.release2Done = !summary.release2Done; next.release2Date = next.release2Done ? todayISO() : ""; }
    setSummary(next);
    try {
      const ref = doc(db, "projects", projectId, "completion", "summary");
      const exists = (await getDoc(ref)).exists();
      const payload = { ...next, dlpEnd, updatedAt: serverTimestamp() };
      if (exists) await updateDoc(ref, payload); else await setDoc(ref, { ...payload, createdAt: serverTimestamp() });
    } catch (e) { alert(`Save failed: ${e.message || e}`); }
  }

  if (!projectId) {
    return <div style={s.page}><div style={s.card}><p style={s.sub}>Select a project to manage completion & defects.</p></div></div>;
  }

  const pcIssued = !!summary.pcDate;
  const dlpChip = dlpDaysLeft === null ? null
    : dlpDaysLeft < 0 ? { ...STATUS_META.Closed, label: "DLP ended" }
    : dlpDaysLeft <= 30 ? { ...STATUS_META.Overdue, label: `${dlpDaysLeft} days to end of DLP` }
    : { ...STATUS_META.Open, label: `${dlpDaysLeft} days to end of DLP` };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <h1 style={s.title}>Completion & Defects</h1>
        <p style={s.sub}>Practical Completion, the defects liability period, snag close-out and the GCC retention-release milestones tied to them.</p>
      </div>

      {/* ── KPI strip ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <div style={s.statTile}><p style={s.statLabel}>Practical Completion</p><p style={{ ...s.statValue, fontSize: 15 }}>{pcIssued ? fmtDate(summary.pcDate) : "Not issued"}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>End of DLP</p><p style={{ ...s.statValue, fontSize: 15 }}>{dlpEnd ? fmtDate(dlpEnd) : "—"}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Open defects</p><p style={{ ...s.statValue, color: defectStats.open ? C.warning : C.success }}>{defectStats.open}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Overdue defects</p><p style={{ ...s.statValue, color: defectStats.overdue ? C.danger : C.ink }}>{defectStats.overdue}</p></div>
        <div style={s.statTile}><p style={s.statLabel}>Retention outstanding</p><p style={{ ...s.statValue, fontSize: 15 }}>{fmtR(retentionOutstanding)}</p></div>
      </div>

      {/* ── Practical Completion + DLP ── */}
      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <h3 style={{ ...s.h3, margin: 0 }}>Certificate of Practical Completion & DLP</h3>
          {dlpChip && <span style={s.chip(dlpChip)}>{dlpChip.label}</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
          <div><label style={s.lbl}>PC certificate no</label><input style={s.inp} value={summary.pcCertificateNo} onChange={(e) => setSumField("pcCertificateNo", e.target.value)} placeholder="PCC-01" /></div>
          <div><label style={s.lbl}>Date of Practical Completion</label><input type="date" style={s.inp} value={summary.pcDate} onChange={(e) => setSumField("pcDate", e.target.value)} /></div>
          <div><label style={s.lbl}>Issued by</label><input style={s.inp} value={summary.pcIssuedBy} onChange={(e) => setSumField("pcIssuedBy", e.target.value)} placeholder="Engineer" /></div>
          <div><label style={s.lbl}>DLP (months)</label><input type="number" style={s.inp} value={summary.dlpMonths} onChange={(e) => setSumField("dlpMonths", e.target.value)} /></div>
          <div><label style={s.lbl}>Total retention held (R)</label><input type="number" style={s.inp} value={summary.retentionHeld} onChange={(e) => setSumField("retentionHeld", e.target.value)} placeholder="0.00" /></div>
          <div><label style={s.lbl}>Final Approval certificate no</label><input style={s.inp} value={summary.finalApprovalCertNo} onChange={(e) => setSumField("finalApprovalCertNo", e.target.value)} placeholder="FAC-01" /></div>
        </div>
        <div style={{ marginTop: 14 }}>
          <button style={s.btnAccent} onClick={saveSummary} disabled={savingSummary}>{savingSummary ? "Saving…" : "Save completion details"}</button>
        </div>
      </div>

      {/* ── Retention release milestones ── */}
      <div style={s.card}>
        <h3 style={s.h3}>Retention release milestones (GCC 6.10.3)</h3>
        <div style={{ display: "grid", gap: 10 }}>
          <div style={s.milestone}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 13 }}>1 — Half retention at Practical Completion</div>
              <div style={s.sub}>Due on PC: {pcIssued ? fmtDate(summary.pcDate) : "— (set PC date)"} · {fmtR(release1Amt)}{summary.release1Done && summary.release1Date ? ` · released ${fmtDate(summary.release1Date)}` : ""}</div>
            </div>
            <button style={summary.release1Done ? s.btn : s.btnAccent} onClick={() => toggleRelease(1)}>
              {summary.release1Done ? "Mark not released" : "Mark released"}
            </button>
          </div>
          <div style={s.milestone}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 13 }}>2 — Balance of retention at end of DLP</div>
              <div style={s.sub}>Due at end of DLP: {dlpEnd ? fmtDate(dlpEnd) : "— (set PC date & DLP)"} · {fmtR(release2Amt)}{summary.release2Done && summary.release2Date ? ` · released ${fmtDate(summary.release2Date)}` : ""}</div>
            </div>
            <button style={summary.release2Done ? s.btn : s.btnAccent} onClick={() => toggleRelease(2)}>
              {summary.release2Done ? "Mark not released" : "Mark released"}
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 24, marginTop: 14, flexWrap: "wrap" }}>
          <div><span style={s.lbl}>Released to date</span><span style={{ ...s.mono, fontWeight: 800 }}>{fmtR(releasedToDate)}</span></div>
          <div><span style={s.lbl}>Outstanding</span><span style={{ ...s.mono, fontWeight: 800, color: retentionOutstanding > 0 ? C.warning : C.success }}>{fmtR(retentionOutstanding)}</span></div>
        </div>
      </div>

      {/* ── Snag / defects list ── */}
      <div style={s.card}>
        <h3 style={s.h3}>{editingId ? "Edit defect" : "Add defect / snag"}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          <div><label style={s.lbl}>Ref</label><input style={s.inp} value={defForm.ref} onChange={(e) => setDefField("ref", e.target.value)} placeholder="auto (DEF-001)" /></div>
          <div style={{ gridColumn: "span 2" }}><label style={s.lbl}>Description</label><input style={s.inp} value={defForm.description} onChange={(e) => setDefField("description", e.target.value)} placeholder="Cracked screed at pump house" /></div>
          <div><label style={s.lbl}>Location</label><input style={s.inp} value={defForm.location} onChange={(e) => setDefField("location", e.target.value)} placeholder="Ch 1+200" /></div>
          <div><label style={s.lbl}>Drawing ref</label><input style={s.inp} value={defForm.drawingRef} onChange={(e) => setDefField("drawingRef", e.target.value)} placeholder="C-101" /></div>
          <div><label style={s.lbl}>Raised</label><input type="date" style={s.inp} value={defForm.raisedDate} onChange={(e) => setDefField("raisedDate", e.target.value)} /></div>
          <div><label style={s.lbl}>Target close-out</label><input type="date" style={s.inp} value={defForm.targetDate} onChange={(e) => setDefField("targetDate", e.target.value)} /></div>
          <div><label style={s.lbl}>Status</label><select style={s.inp} value={defForm.status} onChange={(e) => setDefField("status", e.target.value)}>{DEFECT_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button style={s.btnAccent} onClick={saveDefect}>{editingId ? "Update defect" : "Add defect"}</button>
          {editingId && <button style={s.btn} onClick={resetDefect}>Cancel</button>}
        </div>

        <div style={{ overflowX: "auto", marginTop: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Ref", "Description", "Location", "Drawing", "Raised", "Target", "Status", "Closed", "Actions"].map((h) => <th key={h} style={s.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {defects.map((d) => {
                const st = defectState(d);
                const meta = STATUS_META[st] || STATUS_META.Open;
                return (
                  <tr key={d.id}>
                    <td style={{ ...s.td, ...s.mono, fontWeight: 700 }}>{d.ref || "—"}</td>
                    <td style={s.td}>{d.description || "—"}</td>
                    <td style={s.td}>{d.location || "—"}</td>
                    <td style={{ ...s.td, ...s.mono }}>{d.drawingRef || "—"}</td>
                    <td style={{ ...s.td, ...s.mono }}>{fmtDate(d.raisedDate)}</td>
                    <td style={{ ...s.td, ...s.mono }}>{fmtDate(d.targetDate)}</td>
                    <td style={s.td}><span style={s.chip(meta)}>{st}</span></td>
                    <td style={{ ...s.td, ...s.mono }}>{d.status === "Closed" ? fmtDate(d.closedDate) : "—"}</td>
                    <td style={s.td}>
                      <div style={{ display: "inline-flex", gap: 6 }}>
                        <button style={s.btnSmall} onClick={() => editDefect(d)}>Edit</button>
                        <button style={s.btnDanger} onClick={() => removeDefect(d.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!defects.length && <tr><td style={{ ...s.td, color: C.inkMuted }} colSpan={9}>No defects logged.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default React.memo(CompletionDefectsSection);
